import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import parse from 'style-to-object';
import './draggable-entity-card.css';
import {
	ENTITY_FA_ICON_NAMES,
	getCanvasEntityDOMId,
	resolveEntityColorClass,
	getCardGradientStatement,
	fromDisabledReasonsToCardState
} from '../../services/editor/ui';
import Button from '../../lib/button/Button';
import { Canvas } from '../../lib/canvas';
import TestEntityButton from './TestEntityButton';
import {
	ChangeSet,
	EntityType,
	LoopState,
	ProjectState,
	CanvasEntityConnectionDisabledReason,
	CanvasEntityDisabledReason,
	CanvasEntityOtherDisabledReason,
	CanvasEntityTestingDisabledReason,
	ALL_CONNECTION_DISABLED_REASONS,
	removeDisabledReasons,
	addDisabledReasons,
	READABLE_ENTITY_TYPES,
	BUILT_IN_BASE_ENTITY_IDS,
	lowercaseFirstLetter,
	StateMutationAction,
	ChangeSetEntityChangeType,
	createStateFromTypeAndSubscribe,
	getDeclarationOutputedError,
	IActionOutputMapTransfer,
	IValueDescriptorTransfer,
	OutputMapParentChildRelation,
	OutputMapState,
  safeResolveEntityName
} from '@elyx-code/project-logic-tree';
import './collapse-entity-button.css';
import CanvasPopup, { useCanvasPopup } from '../../services/editor/CanvasPopup';
import CanvasDropdownMenuCard from '../../lib/dropdown/CanvasDropdownMenuCard';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import OutputMapProperty from './OutputMapProperty';
import InputMapProperty from './InputMapProperty';
import ErrorMapProperty from './ErrorMapProperty';
import ExpandPropertiesIconButton from './expand-properties-icon-button/ExpandPropertiesIconButton';
import SuccessFailureNodes from './SuccessFailureNodes';
import MethodDeclaration from './MethodDeclaration';
import LogicErrorIconMenu from './logic-error/LogicErrorIconMenu';
import { useNodeDescriptionPopup } from './useNodeDescriptionPopup';
import { ICanvasElementComponentProps } from '../../services/editor/CanvasElement';
import { EditorService } from '../../services/editor';
import MainValueOutput from './MainValueOutput';
import UnreachableMarker from './UnreachableMarker';
import { popupNotification } from '../../popup-notification';

interface ILoopCardProps extends ICanvasElementComponentProps {
	entity: LoopState;
}

const LoopCard = (props: ILoopCardProps) => {
	const rootElementRef = useRef(null);

	useEffect(() => {
		const element = rootElementRef.current;

		if (!!element && document.contains(element)) {
			props.onMount?.(element);
		}

		// When the component is unmounted
		return () => {
			props.onUnmount?.();
		};
	}, []);

	useEffect(() => {
		const element = rootElementRef.current;

		props.onUpdate?.(element);
	});

	useLayoutEffect(() => {
		props.onBeforeUpdate?.();
	}, []);

	const [updateKey, setValue] = useState(0);

	const useForceUpdate = () => {
		return () => setValue((value) => value + 1);
	};
	const forceUpdate = useForceUpdate(); // Update the component

	useNodeDescriptionPopup({
		entity: props.entity,
		canvasElement: props.canvasElement,
		project: props.project
	});

	const [expandedError, setExpandedError] = useState<boolean>(false);

	const [disabledReasons, setDisabledReasons] = useState<
		CanvasEntityDisabledReason[]
	>([]);

	const entity = props.entity;

	const memoizedCanvasPopupBody = useCallback(
		(popup: CanvasPopup) => (
			<CanvasPopupBaseComponent
				id={
					'canvas-small-entity-card-menu-button-' +
					props.entity.id +
					'--popup--body'
				}
			>
				<CanvasDropdownMenuCard
					options={[
						{
							project: props.project,
							onClick: async () => {
								await props.project.remove(props.entity.id);
							},
							label: 'Delete',
							color: 'error',
							iconEnd: 'fa-solid fa-trash-can',
							disabled: !entity.deletable,
							tooltip: entity.deletable
								? 'Forever delete this loop'
								: 'This loop cannot be deleted. Because the system need to refer to it over time.',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--delete'
						},
						{
							project: props.project,
							onClick: async () => {
								props.project?.canvas?.unselectAll();

								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.Loop}/${props.entity.id}`
								);
							},
							label: 'Edit',
							iconEnd: 'fa-solid fa-pencil',
							disabled: false,
							tooltip: !props.entity.editable
								? `This ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)} has been locked and can no longer be edited`
								: `Edit this ${lowercaseFirstLetter(
										READABLE_ENTITY_TYPES[props.entity.type]
											.singular
									)}`,
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--edit'
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.clearEntityTestValues(props.entity);
							},
							label: 'Clear test values',
							iconEnd: 'fa-solid fa-eraser',
							disabled: !props.project.hasTestValuesForEntity(props.entity),
							tooltip: props.project.hasTestValuesForEntity(props.entity)
								? 'Clear all test values (purple overrides) for this node'
								: 'No test values to clear for this node',
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--clear-test-values'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											StateMutationAction.RecalculateLoop
										)
									);

								entity.afterAllChildrenInitialized(changeSet);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label: 'Recalculate',
							color: 'warning',
							// Bug icon
							iconEnd: 'fa-solid fa-bug',
							disabled: false,
							// Lower case the first letter of the resolved readable name
							tooltip:
								'Recalculate the state of this ' +
								lowercaseFirstLetter(
									READABLE_ENTITY_TYPES[props.entity.type]
										.shortSingular
								),
							parentCanvasObject: popup.self,
							id:
								'canvas--' +
								props.entity.type +
								'-card--' +
								props.entity.id +
								'--recalculate'
						},
						{
							project: props.project,
							onClick: async () => {
								const changeSet =
									props.project.logic.addChangeSet(
										new ChangeSet(
											props.project.logic,
											ProjectState.sessionAuthor,
											new Date().toISOString(),
											entity,
											false,
											entity.catchesError
												? StateMutationAction.ChangeLoopToThrowErrors
												: StateMutationAction.ChangeLoopToCatchErrors
										)
									);

								entity.metaSync(
									{
										catchesError: !entity.catchesError
									},
									changeSet
								);

								props.project.renderAndCloseChangeSet(
									changeSet
								);
							},
							label:
								entity.catchesError ||
								!!entity.errorCalls.length
									? 'Errors are caught'
									: 'Errors terminate execution',
							color: 'error',
							disabled: !!entity.errorCalls.length,
							iconEnd:
								entity.catchesError ||
								!!entity.errorCalls.length
									? 'fa-solid fa-xmark'
									: 'fa-solid fa-ban',
							subLabel: !!entity.errorCalls.length
								? 'Error call connected nodes'
								: entity.catchesError
									? 'Click to throw error instead'
									: 'Click to catch errors instead',
							tooltip: !!entity.errorCalls.length
								? 'Remove all connected nodes to the error branch if you want to throw the error instead'
								: entity.catchesError
									? 'Currently, if an error occurs, all other branches will continue executing.'
									: 'Currently, if an error occurs it will terminate all ongoing executions. Including paralel branches.',
							parentCanvasObject: popup.self,
							id:
								`canvas--${entity.type}--` +
								props.entity.id +
								'--error-handling'
						},
						{
							project: props.project,
							onClick: async () => {
								navigator.clipboard.writeText(props.entity.id);
								popupNotification({
									text: `Copied ID: ${props.entity.id}`,
									color: 'success'
								});
							},
							label: 'Copy ID',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								'Copy this node ID to clipboard for debugging',
							parentCanvasObject: popup.self,
							id: 'copy-id-' + props.entity.id
						},
						{
							project: props.project,
							onClick: async () => {
								await props.project.recalculateValidateEntity(
									props.entity
								);
								popupNotification({
									text: `Re-validated node successfully`,
									color: 'success'
								});
							},
							label: 'Re-validate',
							color: 'warning',
							iconEnd: 'fa-solid fa-bug',
							tooltip:
								"Re-validate this node's state and its errors",
							parentCanvasObject: popup.self,
							id: 're-validate-' + props.entity.id
						}
					]}
				/>
			</CanvasPopupBaseComponent>
		),
		[entity, props.project, props.canvasElement]
	);

	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id:
				'canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup',
			anchorQuerySelector:
				'#canvas-small-entity-card-menu-button-' + props.entity.id,
			arrowClasses: ['dropdown-menu-card-arrow'],
			parentCanvasObject: props.canvasElement.draggableObject,
			arrowContainerSelector:
				'#canvas-small-entity-card-menu-button-' +
				props.entity.id +
				'--popup--body'
		},
		memoizedCanvasPopupBody
	);

	useEffect(() => {
		// --- Initial Setup Logic ---
		const initialNewDisabledReasons: CanvasEntityOtherDisabledReason[] = [];

		// @ts-ignore
		if (props.entity.suggestion) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.Suggestion
			);
		}

		if (props.project.canvas?.disabled) {
			initialNewDisabledReasons.push(
				CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
			);
		}

		if (!!initialNewDisabledReasons.length) {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					initialNewDisabledReasons
				);
				return config.reasons;
			});
		} else {
			// This handles the case where canvas was disabled but is now enabled
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		}

		const handleCanvasGloballyEnabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};
		const handleCanvasGloballyDisabled = () => {
			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(currentReasons, [
					CanvasEntityOtherDisabledReason.CanvasGloballyDisabled
				]);
				return config.reasons;
			});
		};

		const handleEntityUpdate = (payload: any) => {
			let hasUpdated = false;
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];
			const disabledReasonsToRemove: CanvasEntityTestingDisabledReason[] =
				[];

			props.project.test?.disabledConnectionsNotInTest(
				props.canvasElement.draggableObject,
				props.entity
			);

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true;
			}

			// If it is part of test and it is disabled, enable it
			if (
				(props.project.test?.hasEntity(props.entity) ||
					props.project.test?.getCanBeAddedToTest(props.entity)) &&
				props.canvasElement.draggableObject.disabled
			) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
				hasUpdated = true;
			}

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity)
			) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
				hasUpdated = true;
			} else if (
				!!props.project.test &&
				props.project.test?.hasEntity(props.entity)
			) {
				disabledReasonsToRemove.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
				hasUpdated = true;
			}

			if (newDisabledReasons.length || disabledReasonsToRemove.length) {
				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newDisabledReasons
					);
					const finalConfig = removeDisabledReasons(
						config.reasons,
						disabledReasonsToRemove
					);
					return finalConfig.reasons;
				});
				// hasUpdated is already true
			}

			if (!hasUpdated) {
				forceUpdate();
			}
		};

		const handleTestSetupStarted = () => {
			// If there is a test instance and the current entity is not in the test
			// or in the entities that can be added to the test,
			// disable the draggable objects
			const newDisabledReasons: CanvasEntityTestingDisabledReason[] = [];

			if (
				!!props.project.test &&
				!props.project.test?.hasEntity(props.entity) &&
				!props.project.test?.getCanBeAddedToTest(props.entity)
			) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
				props.project.test?.disabledConnectionsNotInTest(
					props.canvasElement.draggableObject,
					props.entity
				);

				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				);
			}

			if (!props.project.test?.hasEntity(props.entity)) {
				newDisabledReasons.push(
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest
				);
			}

			setDisabledReasons((currentReasons) => {
				const config = addDisabledReasons(
					currentReasons,
					newDisabledReasons
				);
				return config.reasons;
			});
		};

		const handleExecutionStart = () => {
			if (!!props.project.test?.hasEntity(props.entity)) {
				forceUpdate();
			}
		};

		const handleTestExited = () => {
			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(currentReasons, [
					CanvasEntityTestingDisabledReason.NotPartOfOngoingTest,
					CanvasEntityTestingDisabledReason.CannotBeAddedToTest
				]);
				return config.reasons;
			});

			if (props.canvasElement.draggableObject.disabled) {
				props.project.canvas?.enable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);
			}

			props.canvasElement.draggableObject.connections.forEach(
				(connection) => {
					connection.enable();
					connection.element.classList.remove('disabled');

					if (connection.complementaryLineElement) {
						connection.complementaryLineElement.classList.remove(
							'disabled'
						);
					}
				}
			);
		};

		const handleCanvasConnectionStart = (payload: any) => {
			if (!payload.compatible?.[props.entity.id]) {
				props.project.canvas?.disable(
					props.canvasElement.draggableObject,
					{
						quiet: true
					}
				);

				const newOwnConnectionDisabledReasons: CanvasEntityConnectionDisabledReason[] =
					payload.incompatible?.[props.entity.id]?.reasons || [];

				setDisabledReasons((currentReasons) => {
					const config = addDisabledReasons(
						currentReasons,
						newOwnConnectionDisabledReasons
					);
					return config.reasons;
				});
			} else {
				forceUpdate();
			}
		};

		const handleCanvasConnectionEnd = () => {
			props.project.canvas?.enable(props.canvasElement.draggableObject, {
				quiet: true
			});

			setDisabledReasons((currentReasons) => {
				const config = removeDisabledReasons(
					currentReasons,
					ALL_CONNECTION_DISABLED_REASONS
				);
				return config.reasons;
			});
		};

		props.project.on(
			'canvas-globally-enabled',
			handleCanvasGloballyEnabled
		);
		props.project.on(
			'canvas-globally-disabled',
			handleCanvasGloballyDisabled
		);
		props.project.on(props.entity.id, handleEntityUpdate);
		props.project.on('test-setup-started', handleTestSetupStarted);
		props.project.on('execution-start', handleExecutionStart);
		props.project.on('test-exited', handleTestExited);
		props.project.on(
			'canvas-connection-start',
			handleCanvasConnectionStart
		);
		props.project.on('canvas-connection-end', handleCanvasConnectionEnd);

		return () => {
			props.project.off(
				'canvas-globally-enabled',
				handleCanvasGloballyEnabled
			);
			props.project.off(
				'canvas-globally-disabled',
				handleCanvasGloballyDisabled
			);
			props.project.off(props.entity.id, handleEntityUpdate);
			props.project.off('test-setup-started', handleTestSetupStarted);
			props.project.off('execution-start', handleExecutionStart);
			props.project.off('test-exited', handleTestExited);
			props.project.off(
				'canvas-connection-start',
				handleCanvasConnectionStart
			);
			props.project.off(
				'canvas-connection-end',
				handleCanvasConnectionEnd
			);
		};
	}, [
		props.project,
		props.entity,
		props.canvasElement,
		updateKey,
		// @ts-ignore
		props.entity.suggestion
	]);

	const outputMaps = entity.outputs;
	const inputMaps = entity.inputs;

	const classes = ['canvas-draggable-card'];

	return (
		<div
			ref={rootElementRef}
			className={classes.join(' ')}
			id={getCanvasEntityDOMId(props.entity)}
			{...{
				[Canvas.OBJ_ATTR_NAME]: getCanvasEntityDOMId(props.entity)
			}}
			onClick={(e) => {
				e.stopPropagation();
			}}
		>
			<TestEntityButton
				entity={props.entity}
				canvasElement={props.canvasElement}
				project={props.project}
				parentDisabledReasons={disabledReasons}
			/>
			{/* Floats "Unreachable!" above the right side of the header when
				the entity carries the blocked-reachability error. Same DOM
				placement as VariableScopeMarker on VariableCard so the
				`position: absolute` offsets resolve against the outer
				canvas-draggable-card div. Returns null when reachable. */}
			<UnreachableMarker
				entity={props.entity}
				parentDisabledReasons={disabledReasons}
				project={props.project}
				draggableObject={props.canvasElement.draggableObject}
			/>
			<div
				className={[
					'canvas-draggable-card__body',
					...(!!disabledReasons.length
						? ['canvas-draggable-card__body__disabled']
						: []),
					resolveEntityColorClass(props.entity.type, {
						state: fromDisabledReasonsToCardState(disabledReasons)
					})
				].join(' ')}
			>
				<header
					className="canvas-draggable-card__header"
					{...{
						[Canvas.OBJ_ATTR_NAME]:
							getCanvasEntityDOMId(props.entity) + '--header'
					}}
					style={parse(
						getCardGradientStatement(entity, {
							state: fromDisabledReasonsToCardState(
								disabledReasons
							)
						})
					)}
				>
					<div className="canvas-draggable-card__header__left-block">
						<i
							className={[
								'canvas-draggable-card-header__icon',
								'fa-solid',
								`fa-${ENTITY_FA_ICON_NAMES[entity.type] || 'rectangle-list'}`
							].join(' ')}
						/>
						<span className="canvas-draggable-card__header__labels">
							<h3>
								{safeResolveEntityName(
									entity,
									props.project.logic
								)}
							</h3>
							<p>
								{READABLE_ENTITY_TYPES[entity.type]?.singular}
							</p>
						</span>
					</div>
					<div className="canvas-draggable-card__header__actions">
						<LogicErrorIconMenu
							entity={entity}
							project={props.project}
							canvasElement={props.canvasElement}
							parentDisabledReasons={disabledReasons}
						/>
						<Button
							variant="text"
							color="grey"
							iconEnd="fa-solid fa-ellipsis-v"
							ref={canvasPopup.anchorRef}
							size="small"
							className="canvas-small-entity-card-menu-button"
							id={`canvas-small-entity-card-menu-button-${entity.id}`}
							disabled={!!disabledReasons.length}
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
								canvasPopup?.toggle();
							}}
							onDoubleClick={(
								e: React.MouseEvent<HTMLButtonElement>
							) => {
								e.stopPropagation();
								if (disabledReasons.length) return;
								props.project?.canvas?.unselectAll();
								EditorService.navigate(
									`${props.project?.editorBasePath}/entity/${EntityType.Loop}/${props.entity.id}`
								);
							}}
						/>
					</div>
					<SuccessFailureNodes
						entity={entity}
						draggableObject={props.canvasElement.draggableObject}
						project={props.project}
						parentDisabledReasons={disabledReasons}
					/>
				</header>
				<div className="canvas-draggable-card__content">
					{!!entity.error ? (
						<ErrorMapProperty
							outputMap={entity.error}
							entity={entity}
							draggableObject={
								props.canvasElement.draggableObject
							}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					) : (
						<>
							{expandedError && (
								<ErrorMapProperty
									outputMap={entity.error || null}
									entity={entity}
									draggableObject={
										props.canvasElement.draggableObject
									}
									project={props.project}
									parentDisabledReasons={disabledReasons}
								/>
							)}

							<ExpandPropertiesIconButton
								project={props.project}
								expanded={expandedError}
								draggableObject={
									props.canvasElement.draggableObject
								}
								onClick={() => {
									const errorDeclaration =
										getDeclarationOutputedError(entity);

									// Create error output-map entity before expanding the error for the first time
									if (!entity.error && !expandedError) {
										const actionName =
											StateMutationAction.AddErrorOutputToNode;

										const changeSet =
											props.project.logic.addChangeSet(
												new ChangeSet(
													props.project.logic,
													ProjectState.sessionAuthor,
													new Date().toISOString(),
													entity,
													false,
													actionName
												)
											);

										const newOutputMapTransfer: IActionOutputMapTransfer =
											OutputMapState.new(
												changeSet
											) as IActionOutputMapTransfer;
										newOutputMapTransfer.parent =
											entity.toReference();
										newOutputMapTransfer.declaration =
											(errorDeclaration?.toJSON() as IValueDescriptorTransfer) ||
											null;
										newOutputMapTransfer.parentRelationType =
											OutputMapParentChildRelation.Error;

										const newInstance =
											createStateFromTypeAndSubscribe(
												newOutputMapTransfer,
												props.project.logic
											) as OutputMapState;

										newInstance.hydrateAncestors();
										newInstance.addSelfToProject(changeSet);

										changeSet.add(
											newInstance,
											ChangeSetEntityChangeType.Added
										);

										newInstance.initChildren(changeSet);

										props.project.renderAndCloseChangeSet(
											changeSet
										);
									}

									setExpandedError(!expandedError);
								}}
								id={`expand-inputs-icon-button--${entity.id}`}
								tooltip={
									expandedError
										? 'Click to hide unused error output'
										: 'Click to show unused error output'
								}
								tooltipPlacement="top"
								style={{ marginTop: '6px' }}
								parentDisabledReasons={disabledReasons}
							/>
						</>
					)}

					{entity.getDataType(null)?.entity?.id !==
						BUILT_IN_BASE_ENTITY_IDS['primitive-entity'].void
							.id && (
						<MainValueOutput
							entity={entity}
							canvasElement={props.canvasElement}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					)}
					{inputMaps.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{inputMaps.map((inputMap) => {
								return (
									<InputMapProperty
										key={inputMap.id}
										inputMap={inputMap}
										entity={props.entity}
										canvasElement={props.canvasElement}
										project={props.project}
										parentDisabledReasons={disabledReasons}
									/>
								);
							}, [])}
						</ul>
					)}
					{outputMaps.length > 0 && (
						<ul className="canvas-draggable-card__property-list">
							{outputMaps.map((outputMap) => (
								<OutputMapProperty
									key={outputMap.id}
									outputMap={outputMap}
									entity={props.entity}
									canvasElement={props.canvasElement}
									project={props.project}
									parentDisabledReasons={disabledReasons}
								/>
							))}
						</ul>
					)}
					{entity.body && (
						<MethodDeclaration
							entity={entity.body}
							canvasElement={props.canvasElement}
							project={props.project}
							parentDisabledReasons={disabledReasons}
						/>
					)}
				</div>
			</div>
		</div>
	);
};

export default LoopCard;
